import React from "react";
import fileIcon from "../../../assets/file.svg";
import { formatToolResultAsMarkdown } from "./formatToolResultAsMarkdown";

interface DocEntry {
	doc_id: string;
	entity?: string;
	period_end?: string;
}

interface DealsJson {
	total_matches?: number;
	deals?: Record<string, { count?: number; documents?: DocEntry[] }>;
}

/** Recursively render any JSON value: string, number, array, or nested object. */
function formatJsonValue(value: unknown, depth = 0): React.ReactNode {
	if (value === null || value === undefined) {
		return <span className="trace-doc-scalar trace-doc-null">—</span>;
	}
	if (typeof value === "string") {
		return <span className="trace-doc-scalar">{value}</span>;
	}
	if (typeof value === "number" || typeof value === "boolean") {
		return <span className="trace-doc-scalar">{String(value)}</span>;
	}
	if (Array.isArray(value)) {
		if (value.length === 0) return null;
		return (
			<ul className="trace-doc-array" style={{ paddingLeft: 0 }}>
				{value.map((item, i) => (
					<li key={i} className="trace-doc-array-item">
						{formatJsonValue(item, depth + 1)}
					</li>
				))}
			</ul>
		);
	}
	if (typeof value === "object") {
		const entries = Object.entries(value as Record<string, unknown>).filter(
			([, v]) => v !== null && v !== undefined && v !== "",
		);
		if (entries.length === 0) return null;
		return (
			<ul className="trace-doc-object" style={{ paddingLeft: 0 }}>
				{entries.map(([k, v]) => {
					const isLeaf =
						typeof v === "string" ||
						typeof v === "number" ||
						typeof v === "boolean";
					return (
						<li
							key={k}
							className={`trace-doc-object-entry${isLeaf ? " trace-doc-object-entry--row" : ""}`}
						>
							<span className="trace-doc-object-key">{k}</span>
							<span className="trace-doc-object-val">
								{formatJsonValue(v, depth + 1)}
							</span>
						</li>
					);
				})}
			</ul>
		);
	}
	return null;
}

export function formatToolResultAsDocumentList(summary: string): React.ReactNode {
	try {
		const parsed = JSON.parse(summary);

		// Deals shape: { deals: { ... }, total_matches?: number }
		if (parsed.deals && typeof parsed.deals === "object") {
			const dealsData = parsed as DealsJson;
			const docs: DocEntry[] = [];
			for (const deal of Object.values(dealsData.deals!)) {
				if (Array.isArray(deal.documents)) {
					docs.push(...deal.documents);
				}
			}
			if (docs.length > 0) {
				return (
					<ul className="trace-doc-list">
						{dealsData.total_matches != null && (
							<li className="trace-doc-total">
								{dealsData.total_matches} matches
							</li>
						)}
						{docs.map((doc) => (
							<li key={doc.doc_id} className="trace-doc-item">
								<img src={fileIcon} alt="" className="trace-doc-icon" />
								<div className="trace-doc-info">
									<span className="trace-doc-entity" data-doc-id={doc.doc_id}>
										{doc.entity ?? doc.doc_id}
									</span>
									{doc.period_end && (
										<span className="trace-doc-period">{doc.period_end}</span>
									)}
								</div>
							</li>
						))}
					</ul>
				);
			}
		}

		// Generic fallback: recursively render any shape
		const rendered = formatJsonValue(parsed);
		return rendered ? (
			<div className="trace-doc-generic">{rendered}</div>
		) : (
			formatToolResultAsMarkdown(summary)
		);
	} catch {
		return formatToolResultAsMarkdown(summary);
	}
}
